import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { History } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import apiService from '../../services/apiService';
import RunHistoryPanel from '../shared/RunHistory/RunHistoryPanel';
import styles from './RunHistory.module.scss';

const AGENTS_PAGE_SIZE = 100;

/**
 * Run History — every run the signed-in user has triggered, across agents
 * (`GET /agent-metrics/runs`, permission `dashboard`). Clicking a row opens the
 * shared Run Details drawer.
 *
 * `?agent_id=` pre-selects an agent, so an agent screen can deep-link here.
 */
const RunHistory = () => {
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const [agents, setAgents] = useState([]);
  const [agentsError, setAgentsError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const agentId = searchParams.get('agent_id') || '';

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: isDarkMode ? 'dark' : 'light', primary: { main: '#e36250' } },
        typography: { fontFamily: '"Poppins", "sans-serif"' },
      }),
    [isDarkMode]
  );

  // The filter needs every agent, and the list endpoint caps a page at
  // AGENTS_PAGE_SIZE — so page 1 first, then the rest off `total_pages`.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const first = await apiService.getAgents(1, AGENTS_PAGE_SIZE);
        if (cancelled) return;
        let combined = [...(first?.agents || [])];
        const totalPages = Number(first?.total_pages) || 1;
        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              apiService.getAgents(i + 2, AGENTS_PAGE_SIZE)
            )
          );
          if (cancelled) return;
          rest.forEach((res) => {
            combined = combined.concat(res?.agents || []);
          });
        }
        setAgents(combined);
      } catch (err) {
        // Non-fatal: the run list still works, it just can't be filtered by agent.
        console.error('RunHistory: failed to load agents for the filter', err);
        if (!cancelled) setAgentsError('Could not load agents for the filter.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAgentChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('agent_id', value);
    else next.delete('agent_id');
    setSearchParams(next, { replace: true });
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={styles.layout}>
        <header className={styles.headerCard}>
          <Box>
            <Box className={styles.headerTitleRow}>
              <History size={22} color="#e36250" aria-hidden />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Run History
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Every run you've triggered — what was asked, what came back, and which tools ran.
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Agent</InputLabel>
            <Select label="Agent" value={agentId} onChange={(e) => handleAgentChange(e.target.value)}>
              <MenuItem value="">All agents</MenuItem>
              {agents.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  {a.name || `Agent ${a.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </header>

        {agentsError && (
          <Alert severity="info" sx={{ borderRadius: '12px' }} onClose={() => setAgentsError(null)}>
            {agentsError}
          </Alert>
        )}

        <RunHistoryPanel
          agentId={agentId || null}
          onToast={(message, severity = 'info') => setToast({ open: true, message, severity })}
        />

        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={toast.severity}
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            sx={{ width: '100%', borderRadius: '10px' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
};

export default RunHistory;
